import React, { useRef, useEffect, useState } from 'react';
import Input from './Input';

const GooglePlacesAutocomplete = ({
  onPlaceSelect,
  placeholder = 'Enter address',
  value = '',
  onChange,
  className = '',
  disabled = false,
  types = ['address']
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!apiKey) {
      console.error('Google Maps API key not found in environment variables');
      setHasError(true);
      return;
    }
    
    // Check if Google Maps API is loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleLoaded(true);
    } else {
      // Load Google Maps JavaScript API
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initMap`;
      script.async = true;
      script.defer = true;
      
      // Add callback function to window
      window.initMap = () => {
        setIsGoogleLoaded(true);
      };
      
      script.onload = () => {
        // Script loaded, callback should be called
      };
      
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
        setHasError(true);
      };
      
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isGoogleLoaded && inputRef.current && !autocompleteRef.current) {
      try {
        // Initialize Google Places Autocomplete
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          {
            types: types,
            fields: ['place_id', 'name', 'formatted_address', 'address_components', 'geometry']
          }
        );

        // Add place changed listener
        autocompleteRef.current.addListener('place_changed', () => {
          try {
            const place = autocompleteRef.current.getPlace();

            if (place.place_id) {
              const addressInfo = extractAddressInfo(place);
              setInputValue(addressInfo.streetAddress || addressInfo.formattedAddress || '');
              onPlaceSelect(addressInfo);
            }
          } catch (error) {
            console.error('Error in place_changed listener:', error);
          }
        });
      } catch (error) {
        console.error('Error initializing Google Places Autocomplete:', error);
        setHasError(true);
      }
    }
  }, [isGoogleLoaded, onPlaceSelect, types]);

  const extractAddressInfo = (place) => {
    const addressComponents = place.address_components || [];

    const findComponent = (types, useShortName = false) => {
      const component = addressComponents.find((entry) =>
        types.some((type) => entry.types.includes(type))
      );

      if (!component) {
        return '';
      }

      return useShortName
        ? component.short_name || component.long_name || ''
        : component.long_name || component.short_name || '';
    };

    const streetNumber = findComponent(['street_number']);
    const route = findComponent(['route']);
    const neighborhood = findComponent(['neighborhood', 'sublocality', 'sublocality_level_1']);
    const city = findComponent(['locality', 'postal_town', 'sublocality_level_1', 'administrative_area_level_2']);
    const stateLong = findComponent(['administrative_area_level_1', 'administrative_area_level_2']);
    const stateCode = findComponent(['administrative_area_level_1', 'administrative_area_level_2'], true);
    const country = findComponent(['country']);
    const countryCode = findComponent(['country'], true);
    const postalCode = findComponent(['postal_code']);

    const streetAddress = [streetNumber, route].filter(Boolean).join(' ') || route || place.formatted_address || place.name || '';

    return {
      placeId: place.place_id,
      formattedAddress: place.formatted_address || place.name || '',
      streetAddress,
      neighborhood,
      city,
      state: stateLong || stateCode || '',
      stateCode,
      country,
      countryCode,
      postalCode,
      zipCode: postalCode,
      coordinates: place.geometry?.location
        ? {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        : null
    };
  };

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
    onChange?.(event);
  };

  if (hasError) {
    return (
      <Input
        ref={inputRef}
        placeholder="Google Places not available"
        value={inputValue}
        onChange={handleInputChange}
        className={className}
        disabled={true}
      />
    );
  }

  if (!isGoogleLoaded) {
    return (
      <Input
        ref={inputRef}
        placeholder="Loading Google Places..."
        value={inputValue}
        onChange={handleInputChange}
        className={className}
        disabled={true}
      />
    );
  }

  return (
    <Input
      ref={inputRef}
      placeholder={placeholder}
      value={inputValue}
      onChange={handleInputChange}
      className={className}
      disabled={disabled}
    />
  );
};

export default GooglePlacesAutocomplete; 
